import {styled} from '@linaria/react';
import React from 'react';

import type {Color} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/color.types';
import type {Money} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/money.types';
import {useFormatCurrency} from '@aboutyou/i18n';
import {COLORS, FONT_SIZES, FONT_WEIGHTS, Text} from '@aboutyou/ui-components';

interface PriceSummaryRowProps {
  label: string;
  amount: Money;
  isStrikeThrough: boolean;
  foregroundColor: Color;
  backgroundColor?: Color;
  className?: string;
  secondaryPriceLabel?: string;
}

export const PriceSummaryRow: React.FC<PriceSummaryRowProps> = ({
  amount,
  foregroundColor,
  isStrikeThrough,
  label,
  backgroundColor,
  className,
  secondaryPriceLabel,
}) => {
  const formatCurrency = useFormatCurrency();

  return (
    <Container
      data-testid="priceSummaryRow"
      $backgroundColor={backgroundColor?.hex}
      $foregroundColor={foregroundColor.hex}
      className={className}
    >
      <Label data-testid="priceSummaryRowLabel">{label}</Label>
      <Amount
        $isStrikeThrough={isStrikeThrough}
        data-testid="priceSummaryRowAmount"
      >
        <Currency>
          {formatCurrency(amount.amount, amount.currencyCode)}
        </Currency>
        {secondaryPriceLabel && (
          <SecondaryPriceLabel data-testid="priceSummaryRowSecondaryPriceLabel">
            {secondaryPriceLabel}
          </SecondaryPriceLabel>
        )}
      </Amount>
    </Container>
  );
};

const Container = styled.div<{
  $backgroundColor?: string;
  $foregroundColor?: string;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-radius: 4px;
  background-color: ${props => props.$backgroundColor || 'inherit'};
  color: ${props => props.$foregroundColor || 'inherit'};
  padding: ${props => (props.$backgroundColor ? '0 8px' : 'unset')};
`;

const Label = styled(Text)`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: ${FONT_SIZES.px20};
  color: inherit;
`;

const Amount = styled(Label)<{$isStrikeThrough: boolean}>`
  display: flex;
  gap: 4px;
  align-items: baseline;
  flex-wrap: wrap;
  justify-content: flex-end;

  text-decoration: ${props =>
    props.$isStrikeThrough ? 'line-through' : 'none'};
`;

const Currency = styled.span`
  white-space: nowrap;
`;

const SecondaryPriceLabel = styled(Label)`
  color: ${COLORS['Text.Grey']};
`;
